import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Col, Input, Row, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReconciliationOutlined,
  WarningOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useCspmPermissions } from '@/hooks/useCspmPermissions';
import {
  CSPM_EXCEPTION_TYPE_LABELS,
  CSPM_RECEIPT_STATUSES,
  CSPM_REQUIREMENT_STATUSES,
  CSPM_RETURN_STATUSES,
  CSPM_SOURCE_TYPE_LABELS,
  type CustomerPropertyException,
  type CustomerPropertyReceipt,
  type CustomerPropertyRequirement,
  type CustomerPropertyReturn,
  type CspmExceptionType,
  type CspmSourceType,
} from '@/constants/cspm';

const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Open: 'processing',
  Posted: 'success',
  Closed: 'cyan',
  Cancelled: 'error',
  UnderDisposition: 'orange',
  Resolved: 'success',
};

const EXCEPTION_STATUSES = ['Open', 'UnderDisposition', 'Closed', 'Cancelled'] as const;

const CustomerPropertyOperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = App.useApp();
  const cspmPerms = useCspmPermissions();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [tab, setTab] = useState(searchParams.get('tab') || 'receipts');
  const attentionOnly = searchParams.get('attention') === '1';

  const issuePortalMut = useMutation({
    mutationFn: (cid: string) => cspmService.issuePortalToken({ customer_id: cid }),
    onSuccess: (data) => {
      const url = `${window.location.origin}${data?.portal_path || ''}`;
      void navigator.clipboard.writeText(url);
      message.success('Portal link copied to clipboard');
    },
    onError: (e: unknown) => {
      message.error((e as Error)?.message || 'Could not issue portal link');
    },
  });

  const { data: customers = [] } = useActiveCustomers();
  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    customerOptions.forEach((c) => map.set(c.value, c.label));
    return map;
  }, [customerOptions]);

  const listFilters = useMemo(
    () => ({
      status,
      customer_id: customerId,
      take: 100,
    }),
    [status, customerId]
  );

  const receiptFilters = useMemo(
    () => ({
      ...listFilters,
      status: attentionOnly ? undefined : status,
      attention: attentionOnly ? true : undefined,
    }),
    [listFilters, status, attentionOnly]
  );

  const { data: receiptsData, isLoading: loadingReceipts } = useQuery({
    queryKey: ['cspm', 'receipts', receiptFilters],
    queryFn: () => cspmService.listReceipts(receiptFilters),
    enabled: tab === 'receipts' || attentionOnly,
  });

  const { data: attentionReceiptsData } = useQuery({
    queryKey: ['cspm', 'receipts', 'attention-count', customerId],
    queryFn: () =>
      cspmService.listReceipts({ attention: true, customer_id: customerId, take: 100 }),
  });

  const { data: requirementsData, isLoading: loadingRequirements } = useQuery({
    queryKey: ['cspm', 'requirements', listFilters],
    queryFn: () => cspmService.listRequirements(listFilters),
    enabled: tab === 'requirements',
  });

  const { data: exceptionsData, isLoading: loadingExceptions } = useQuery({
    queryKey: ['cspm', 'exceptions', listFilters],
    queryFn: () => cspmService.listExceptions(listFilters),
    enabled: tab === 'exceptions' || attentionOnly,
  });

  const { data: returnsData, isLoading: loadingReturns } = useQuery({
    queryKey: ['cspm', 'returns', listFilters],
    queryFn: () => cspmService.listReturns(listFilters),
    enabled: tab === 'returns',
  });

  const attentionReceiptCount = useMemo(
    () => attentionReceiptsData?.total ?? attentionReceiptsData?.rows?.length ?? 0,
    [attentionReceiptsData]
  );

  const openExceptionCount = useMemo(
    () =>
      (exceptionsData?.rows ?? []).filter(
        (r) => r.status === 'Open' || r.status === 'UnderDisposition'
      ).length,
    [exceptionsData]
  );

  const q = search.trim().toLowerCase();

  const receipts = useMemo(() => {
    let rows = receiptsData?.rows ?? [];
    // Server already filters when attention=1 (inspection hold OR custody ready/in workshop)
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.receipt_number,
        r.customer_id,
        customerNameById.get(r.customer_id),
        r.source_document_type,
        r.source_document_id,
        r.customer_reference,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [receiptsData, q, customerNameById]);

  const requirements = useMemo(() => {
    const rows = requirementsData?.rows ?? [];
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.requirement_number,
        r.customer_id,
        customerNameById.get(r.customer_id),
        r.source_document_type,
        r.source_document_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [requirementsData, q, customerNameById]);

  const exceptions = useMemo(() => {
    let rows = exceptionsData?.rows ?? [];
    if (attentionOnly) {
      rows = rows.filter((r) => r.status === 'Open' || r.status === 'UnderDisposition');
    }
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.exception_number,
        r.customer_id,
        customerNameById.get(r.customer_id),
        r.exception_type,
        r.receipt_id,
        r.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [exceptionsData, q, customerNameById, attentionOnly]);

  const returns = useMemo(() => {
    const rows = returnsData?.rows ?? [];
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.return_number,
        r.customer_id,
        customerNameById.get(r.customer_id),
        r.receipt_id,
        r.source_document_type,
        r.source_document_id,
        r.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [returnsData, q, customerNameById]);

  const statusOptions: Array<{ value: string; label: string }> =
    tab === 'receipts'
      ? CSPM_RECEIPT_STATUSES.map((s) => ({ value: s, label: s }))
      : tab === 'requirements'
        ? CSPM_REQUIREMENT_STATUSES.map((s) => ({ value: s, label: s }))
        : tab === 'returns'
          ? CSPM_RETURN_STATUSES.map((s) => ({ value: s, label: s }))
          : EXCEPTION_STATUSES.map((s) => ({ value: s, label: s }));

  const setTabAndUrl = (next: string) => {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };

  const toggleAttention = () => {
    const nextParams = new URLSearchParams(searchParams);
    if (attentionOnly) nextParams.delete('attention');
    else nextParams.set('attention', '1');
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="space-between" align="middle" gutter={[12, 12]}>
            <Col>
              <h2 style={{ margin: 0 }}>Externally Owned Property</h2>
              <Text type="secondary">
                Requirements, receipts, exceptions, returns, and reconciliation
              </Text>
            </Col>
            <Col>
              <Space wrap>
                {cspmPerms.canPortalIssue ? (
                  <Button
                    icon={<LinkOutlined />}
                    disabled={!customerId}
                    loading={issuePortalMut.isPending}
                    onClick={() => {
                      if (!customerId) {
                        message.warning('Select a customer first');
                        return;
                      }
                      issuePortalMut.mutate(customerId);
                    }}
                  >
                    Issue portal link
                  </Button>
                ) : null}
                <Button
                  icon={<WarningOutlined />}
                  type={attentionOnly ? 'primary' : 'default'}
                  danger={attentionOnly}
                  onClick={toggleAttention}
                >
                  Attention
                  {attentionReceiptCount + openExceptionCount > 0
                    ? ` (${attentionReceiptCount + openExceptionCount})`
                    : ''}
                </Button>
                {cspmPerms.canReconcile && (
                  <Button
                    icon={<ReconciliationOutlined />}
                    onClick={() => navigate('/cspm/reconciliations')}
                  >
                    Reconciliation
                  </Button>
                )}
                {cspmPerms.canReturn && (
                  <Button onClick={() => navigate('/cspm/returns/new')}>New return</Button>
                )}
                {cspmPerms.canRequirementCreate && (
                  <Button onClick={() => navigate('/cspm/requirements/new')}>New requirement</Button>
                )}
                {cspmPerms.canReceiptCreate && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/cspm/receipts/new')}
                  >
                    New receipt
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          {(attentionReceiptCount > 0 || openExceptionCount > 0) && (
            <Alert
              type="warning"
              showIcon
              message="Attention required"
              description={`${attentionReceiptCount} EOP receipt(s) need attention (inspection hold or repair custody) · ${openExceptionCount} open exception(s)`}
              action={
                <Button size="small" onClick={toggleAttention}>
                  {attentionOnly ? 'Show all' : 'Filter attention'}
                </Button>
              }
            />
          )}

          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search number / customer / source / reference"
              />
            </Col>
            <Col xs={24} md={7}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Status"
                value={status}
                onChange={setStatus}
                options={statusOptions}
              />
            </Col>
            <Col xs={24} md={7}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder="Customer"
                value={customerId}
                onChange={setCustomerId}
                options={customerOptions}
              />
            </Col>
          </Row>

          <Tabs
            activeKey={tab}
            onChange={setTabAndUrl}
            items={[
              {
                key: 'receipts',
                label: `Receipts${attentionReceiptCount ? ` (${attentionReceiptCount} attn)` : ''}`,
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingReceipts}
                    dataSource={receipts}
                    pagination={{ pageSize: 20 }}
                    locale={{
                      emptyText: attentionOnly ? 'No receipts needing attention' : 'No receipts',
                    }}
                    onRow={(row: CustomerPropertyReceipt) => ({
                      onClick: () => navigate(`/cspm/receipts/${row.id}`),
                      style: { cursor: 'pointer' },
                    })}
                    columns={[
                      { title: 'EOP #', dataIndex: 'receipt_number', width: 140 },
                      {
                        title: 'Customer',
                        dataIndex: 'customer_id',
                        render: (v: string) => customerNameById.get(v) || v,
                      },
                      {
                        title: 'Source',
                        key: 'source',
                        render: (_: unknown, row: CustomerPropertyReceipt) =>
                          CSPM_SOURCE_TYPE_LABELS[row.source_document_type as CspmSourceType] ||
                          row.source_document_type,
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 110,
                        render: (v: string) => (
                          <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
                        ),
                      },
                      {
                        title: 'Inspection',
                        dataIndex: 'inspection_status',
                        width: 120,
                        render: (v: string, row: CustomerPropertyReceipt) =>
                          row.inspection_required ? (
                            <Tag
                              color={
                                v === 'Pending' ? 'orange' : v === 'Failed' ? 'error' : 'success'
                              }
                            >
                              {v || 'Required'}
                            </Tag>
                          ) : (
                            '—'
                          ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'requirements',
                label: 'Requirements',
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingRequirements}
                    dataSource={requirements}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: 'No requirements' }}
                    onRow={(row: CustomerPropertyRequirement) => ({
                      onClick: () => navigate(`/cspm/requirements/${row.id}`),
                      style: { cursor: 'pointer' },
                    })}
                    columns={[
                      { title: 'Number', dataIndex: 'requirement_number', width: 140 },
                      {
                        title: 'Customer',
                        dataIndex: 'customer_id',
                        render: (v: string) => customerNameById.get(v) || v,
                      },
                      {
                        title: 'Source',
                        dataIndex: 'source_document_type',
                        render: (v: string) =>
                          CSPM_SOURCE_TYPE_LABELS[v as CspmSourceType] || v,
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 120,
                        render: (v: string) => (
                          <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'exceptions',
                label: `Exceptions${openExceptionCount ? ` (${openExceptionCount})` : ''}`,
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingExceptions}
                    dataSource={exceptions}
                    pagination={{ pageSize: 20 }}
                    locale={{
                      emptyText: attentionOnly ? 'No open exceptions' : 'No exceptions',
                    }}
                    onRow={(row: CustomerPropertyException) => ({
                      onClick: () =>
                        row.receipt_id ? navigate(`/cspm/receipts/${row.receipt_id}`) : undefined,
                      style: { cursor: row.receipt_id ? 'pointer' : undefined },
                    })}
                    columns={[
                      { title: 'Number', dataIndex: 'exception_number', width: 140 },
                      {
                        title: 'Type',
                        dataIndex: 'exception_type',
                        render: (v: string) =>
                          CSPM_EXCEPTION_TYPE_LABELS[v as CspmExceptionType] || v,
                      },
                      {
                        title: 'Customer',
                        dataIndex: 'customer_id',
                        render: (v: string) => customerNameById.get(v) || v,
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 140,
                        render: (v: string) => (
                          <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
                        ),
                      },
                      {
                        title: 'Notify',
                        dataIndex: 'notification_ref',
                        ellipsis: true,
                        render: (v: string | null) => v || '—',
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'returns',
                label: 'Returns',
                children: (
                  <Table
                    rowKey="id"
                    loading={loadingReturns}
                    dataSource={returns}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: 'No returns' }}
                    onRow={(row: CustomerPropertyReturn) => ({
                      onClick: () => navigate(`/cspm/returns/${row.id}`),
                      style: { cursor: 'pointer' },
                    })}
                    columns={[
                      { title: 'Number', dataIndex: 'return_number', width: 140 },
                      {
                        title: 'Customer',
                        dataIndex: 'customer_id',
                        render: (v: string) => customerNameById.get(v) || v,
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 120,
                        render: (v: string) => (
                          <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
                        ),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Space>
      </Card>
    </div>
  );
};

export default CustomerPropertyOperationsPage;
